/**
 * Readout — fixed-width monospace label/value pair for telemetry.
 *
 * Designed for dense rows of operational data (latency, RTT, BGP withdraws,
 * AS path length, DNS QPS). Label is left-aligned in muted text; value is
 * right-aligned in primary text, with optional tone for status coloring.
 */

import { tokens } from './tokens.js';

export type ReadoutTone = 'neutral' | 'good' | 'warn' | 'critical';

export interface ReadoutProps {
  readonly label: string;
  readonly value: string | number;
  readonly unit?: string;
  readonly tone?: ReadoutTone;
  /** Total width — a px number or any CSS width. Default `'100%'` (fills its cell). */
  readonly width?: number | string;
}

const toneToColor: Record<ReadoutTone, string> = {
  neutral: 'inherit',
  good: tokens.color.semantic.good,
  warn: tokens.color.semantic.warn,
  critical: tokens.color.semantic.critical,
};

export function Readout({
  label,
  value,
  unit,
  tone = 'neutral',
  width = '100%',
}: ReadoutProps): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'baseline',
        width,
        fontFamily: tokens.type.family.mono,
        fontSize: tokens.type.size.sm,
        lineHeight: 1.4,
      }}
    >
      <span
        style={{
          color: tokens.color.text.muted,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontSize: tokens.type.size.xs,
        }}
      >
        {label}
      </span>
      <span style={{ color: toneToColor[tone], fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {unit !== undefined && (
          <span style={{ color: tokens.color.text.muted, marginLeft: 4 }}>{unit}</span>
        )}
      </span>
    </div>
  );
}
